// src/components/featureAdjudication/featureKeys.js
//
// THE KEYMAP, AS DATA. Forked from wellfinder/adjudicationKeys.js and keeps its
// rule: the handler and the `?` overlay read one source, so the help cannot
// describe a binding that does not exist.
//
// ── WHY THE SHARED KEYS ARE THE SAME KEYS ───────────────────────────────────
//
// j/k, arrows, Esc, ? and the digits mean exactly what they mean in WellFinder.
// A reviewer moves between the two surfaces and should not have to relearn
// navigation; the keys that differ are the ones whose ACTIONS differ.
//
// ── WHAT DIFFERS, AND IT IS STRUCTURAL ──────────────────────────────────────
//
// WellFinder's verdicts are terminal in one keystroke -- `p` approves, sets
// equipment and advances -- because its budget is three seconds across 9,348
// records. That budget does not exist here: there are 103 decisions, not 9,348,
// and each is a boundary judgement rather than a yes/no on a match.
//
// More importantly the verdicts CANNOT be one keystroke:
//
//   reject      needs a reason AND a note. feature_assertions_reject_note_chk
//               requires btrim(note) <> '', so the server refuses a bare reject.
//               Three interactions minimum, and no keymap can compress that.
//   confirm     writable as of migration 081, but out of this slice.
//   merge/split need a selection first and cannot be expressed at all yet --
//               POST /verdicts has no operand list for them.
//
// So `r` opens a picker and a digit chooses, exactly as WellFinder's reject
// does, and that is the shape of every verdict here rather than the exception.
//
// ── confirm IS ONE KEYSTROKE AND reject IS THREE. THAT ASYMMETRY IS A HAZARD ─
//
// A reviewer who habituates to `c` being instant will eventually fire it on a
// group they meant to inspect, and there is NO UNDO: the plane is append-only
// and no assertion type means "I withdraw that". It already happened in the
// other direction -- a reviewer rejected the same observation twice, 11 seconds
// apart, because nothing stopped a second verdict on a decided row.
//
// Three things bound it, and none of them is a confirmation dialog:
//
//   1. `c` confirms the SELECTED MEMBER ONLY. The blast radius of a mis-fire is
//      one assertion, and the selected member is the one drawn in subject
//      fuchsia -- you are looking at what you are about to confirm.
//   2. The N-assertion action takes a MODIFIER. WellFinder rejected modifiers
//      because `a`+shift is two keypresses on a three-second path; that argument
//      does not apply to an action a reviewer takes once per group, and here the
//      extra keypress is exactly the point -- the bigger action should be harder
//      to fire by reflex.
//   3. NEITHER ADVANCES. WellFinder's `p` commits and moves on, which is right
//      at 9,348 records and wrong at 103: advancing after a mis-fire takes the
//      evidence off screen. Staying put means the panel shows what was just
//      written, which is the only correction available when there is no undo.
//
// ── TWO LEVELS, AND FREQUENCY DECIDES WHICH GETS j/k ────────────────────────
//
// With a cell rail beside the groups, "j" has two possible meanings. The first
// attempt resolved that by FOCUS -- the rail owned j/k while it had focus -- and
// it was wrong in a way that browser review caught immediately: selecting a cell
// means clicking a <button> INSIDE the rail, so activeElement stays there and
// j/k moved cells forever after. The map is a plain div and takes no focus, so
// the primary flow never gave it back. Every reviewer hit it on their first
// action.
//
// Focus is an INVISIBLE MODE. Nothing on screen says which of two meanings the
// next keystroke carries, and the reviewer cannot fix what they cannot see.
//
// So frequency decides instead, and it is not close: 103 groups against 14
// cells. j/k are the group keys, always, with no mode. Cells take SHIFT, which
// is the rule this keymap already uses for the coarser unit -- shift-C confirms
// a whole group where c confirms one member.
//
// It also preserves cross-tool muscle memory: in WellFinder j/k advance the
// WORK UNIT, and here the work unit is the group.

// ── j/k MOVE BETWEEN GROUPS, NOT ROWS ───────────────────────────────────────
//
// Measured: 256 of 258 rival edges are RECIPROCAL -- the queue holds both halves
// of nearly every pair. Stepping through rows shows a reviewer the same two
// polygons twice, the second time with the roles swapped, for 84% of the corpus.
// The connected components are the decision unit: 103 groups over 229 rows, and
// the 24-member cluster collapses from 24 stops to one.
//
// Within a group the digits pick a member, which is why they are the same
// binding as WellFinder's candidate selection and not a new one.

export const KEY_ACTIONS = Object.freeze([
  {
    keys: ["c"],
    id: "confirm_one",
    label: "Confirm this polygon",
    detail: "Records it as real, naming the group's other members as what it was judged against",
    group: "Verdict",
    primary: true,
  },
  {
    keys: ["Shift", "C"],
    id: "confirm_group",
    label: "Confirm the whole group",
    detail: "One decision, one assertion per member — for a group that is right as it stands",
    group: "Verdict",
  },
  {
    keys: ["r"],
    id: "reject",
    label: "Reject",
    detail: "Opens the reason picker — a reason and a note are both required",
    group: "Verdict",
    primary: true,
  },
  {
    keys: ["1", "…", "9"],
    id: "select_member",
    label: "Select a polygon in this group",
    detail: "Becomes the subject; the verdict applies to it",
    group: "Group",
  },
  { keys: ["n"], id: "note",  label: "Focus the note field", detail: "Required on a rejection", group: "Group" },
  { keys: ["j", "↓"], id: "next", label: "Next group", group: "Navigate" },
  { keys: ["k", "↑"], id: "prev", label: "Previous group", group: "Navigate" },
  { keys: ["Shift", "J"], id: "next_cell", label: "Next cell", detail: "Takes that cell's lock", group: "Navigate" },
  { keys: ["Shift", "K"], id: "prev_cell", label: "Previous cell", detail: "Takes that cell's lock", group: "Navigate" },
  { keys: ["Esc"], id: "cancel", label: "Cancel the picker or the note", group: "Navigate" },
  { keys: ["?"], id: "help", label: "This list", group: "Navigate" },
]);

export const KEY_GROUPS = ["Verdict", "Group", "Navigate"];

// The seven reasons, in the order the picker offers them, with the digit that
// selects each. Ordered by expected frequency rather than by the enum's storage
// order: a reviewer looking at two overlapping boundaries is usually saying
// "this one is the duplicate" or "this outline is wrong", and those should be
// 1 and 2 rather than buried at 3 and 4.
//
// The VALUES are the aiprime.feature_reject_reason enum and must match it
// exactly -- the server casts and 500s on anything else.
export const REJECT_REASONS = Object.freeze([
  { digit: "1", value: "duplicate",       label: "Duplicate",       detail: "Another polygon in this group is the same feature" },
  { digit: "2", value: "bad_geometry",    label: "Bad geometry",    detail: "Right feature, wrong outline" },
  { digit: "3", value: "not_present",     label: "Not present",     detail: "Nothing is there in the imagery" },
  { digit: "4", value: "wrong_class",     label: "Wrong class",     detail: "Something is there, but not this kind of thing" },
  { digit: "5", value: "imagery_unclear", label: "Imagery unclear", detail: "Cannot be judged from this imagery" },
  { digit: "6", value: "out_of_scope",    label: "Out of scope",    detail: "Real, but not something this survey covers" },
  { digit: "7", value: "other",           label: "Other",           detail: "Say what in the note" },
]);

/**
 * Resolve a KeyboardEvent to an action id, or null.
 *
 * NEVER FIRES WHILE A TEXT INPUT HAS FOCUS. The note is a textarea and `r` is a
 * verdict — typing "rejected, duplicate" into a note would fire reject on the
 * first character. contentEditable is included because a rich text field does
 * not report as INPUT. Esc still works, so a text field is never a trap.
 *
 * Modifiers belong to the browser and the OS: Ctrl+R must stay reload.
 */
export function resolveKey(e) {
  const t = e.target;
  const tag = t?.tagName;
  if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || t?.isContentEditable) {
    return e.key === "Escape" ? "cancel" : null;
  }
  if (e.ctrlKey || e.metaKey || e.altKey) return null;

  // SHIFT IS CHECKED FIRST and only for C. e.key is already case-folded by the
  // browser, so "C" arrives as a distinct key from "c" -- but testing shiftKey
  // explicitly means a keyboard layout where C is unshifted cannot fire the
  // group action by accident.
  if (e.key === "C" && e.shiftKey) return "confirm_group";
  if (e.key === "J" && e.shiftKey) return "next_cell";
  if (e.key === "K" && e.shiftKey) return "prev_cell";
  if (e.shiftKey) return null;

  switch (e.key) {
    case "c": return "confirm_one";
    case "r": return "reject";
    case "n": return "note";
    case "j": case "ArrowDown": return "next";
    case "k": case "ArrowUp": return "prev";
    case "Escape": return "cancel";
    case "?": return "help";
    default:
      if (/^[1-9]$/.test(e.key)) return `digit:${e.key}`;
      return null;
  }
}
